// skipcq: JS-W1028
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  REVIEW_ACTION_LABELS,
  REVIEW_DECISION_LABELS,
  allowedReviewActions,
  applyOutcomeColor,
  applyOutcomeLabel,
  isEditableReviewClassification,
  isReviewRowFrozen,
  missingCandidateCopy,
} from '../../utils/brewery/beerIngestionReview';

function DecisionChip({ action }) {
  if (!action) {
    return null;
  }
  return (
    <Chip
      size="small"
      color={action === 'reject' ? 'default' : 'success'}
      label={REVIEW_DECISION_LABELS[action] ?? action}
    />
  );
}

DecisionChip.propTypes = {
  action: PropTypes.string,
};

function applyErrorText(decision) {
  if (typeof decision?.applyError !== 'string') {
    return '';
  }
  return decision.applyError.trim();
}

function ApplyOutcomeDetails({ decision }) {
  const applyOutcome = decision?.applyOutcome;
  if (!applyOutcome || applyOutcome === 'pending') {
    return null;
  }
  const applyError = applyErrorText(decision);
  const outcomeLabel = applyOutcomeLabel(applyOutcome);
  return (
    <Box>
      <Chip
        size="small"
        color={applyOutcomeColor(applyOutcome)}
        label={outcomeLabel}
      />
      {applyError ? (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {applyError}
        </Typography>
      ) : null}
    </Box>
  );
}

ApplyOutcomeDetails.propTypes = {
  decision: PropTypes.object,
};

export function BeerIngestionReviewStatus({ decision }) {
  const hasDecision = Boolean(decision?.action);
  const applyOutcome = decision?.applyOutcome;
  const showOutcome = Boolean(applyOutcome) && applyOutcome !== 'pending';
  if (!hasDecision && !showOutcome) {
    return (
      <Typography variant="body2" color="text.secondary">—</Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {hasDecision ? <DecisionChip action={decision.action} /> : null}
      {showOutcome ? <ApplyOutcomeDetails decision={decision} /> : null}
    </Stack>
  );
}

BeerIngestionReviewStatus.propTypes = {
  decision: PropTypes.object,
};

function EditRowButton({
  candidateKey,
  editing,
  disabled,
  beerName,
  onClick,
}) {
  const label = editing ? `Done editing ${beerName}` : `Edit ${beerName}`;
  return (
    <Button
      size="small"
      variant="text"
      data-candidate-key={candidateKey}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={editing || undefined}
      aria-label={label}
    >
      {editing ? 'Done' : 'Edit'}
    </Button>
  );
}

EditRowButton.propTypes = {
  candidateKey: PropTypes.string.isRequired,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  beerName: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

function ActionButton({
  action,
  candidateKey,
  disabled,
  loading,
  onClick,
}) {
  return (
    <Button
      size="small"
      variant={action === 'reject' ? 'outlined' : 'contained'}
      data-candidate-key={candidateKey}
      data-action={action}
      onClick={onClick}
      disabled={disabled}
      aria-busy={loading || undefined}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
    >
      {REVIEW_ACTION_LABELS[action]}
    </Button>
  );
}

ActionButton.propTypes = {
  action: PropTypes.string.isRequired,
  candidateKey: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

function ActionButtons({
  candidateKey,
  actions,
  disabled,
  approveBlocked,
  loadingAction,
  onClick,
}) {
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {actions.map((action) => (
        <ActionButton
          key={action}
          action={action}
          candidateKey={candidateKey}
          disabled={disabled || (action === 'approve' && approveBlocked)}
          loading={loadingAction === action}
          onClick={onClick}
        />
      ))}
    </Stack>
  );
}

ActionButtons.propTypes = {
  candidateKey: PropTypes.string.isRequired,
  actions: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
  approveBlocked: PropTypes.bool,
  loadingAction: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

export default function BeerIngestionReviewRowActions({
  candidate,
  decision,
  draft,
  reviewLocked,
  actionLoading = false,
  loadingCandidateKey = null,
  loadingDecisionAction = '',
  editing = false,
  approveBlockedMessage = '',
  onDecisionClick,
  onToggleEdit,
}) {
  const frozen = isReviewRowFrozen(decision);
  const approveBlocked = Boolean(approveBlockedMessage);
  const rowLoading = loadingCandidateKey === candidate.candidateKey;
  const actionsDisabled = reviewLocked || frozen || actionLoading;
  const draftName = typeof draft?.name === 'string' ? draft.name.trim() : '';
  const snapshotName = candidate.currentNormalized?.name || candidate.baselineNormalized?.name;
  const beerName = draftName || snapshotName || 'beer';
  const showEdits = isEditableReviewClassification(candidate.classification)
    && !reviewLocked
    && !frozen
    && typeof onToggleEdit === 'function';
  const actions = allowedReviewActions(candidate.classification);
  const showActions = !reviewLocked && !frozen && actions.length > 0;
  return (
    <Box>
      {candidate.classification === 'missing' ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {missingCandidateCopy()}
        </Typography>
      ) : null}
      {showActions && approveBlockedMessage ? (
        <Typography variant="caption" color="error" display="block" sx={{ mb: 0.5 }}>
          {approveBlockedMessage}
        </Typography>
      ) : null}
      {showEdits || showActions ? (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {showEdits ? (
            <EditRowButton
              candidateKey={candidate.candidateKey}
              editing={editing}
              disabled={actionsDisabled}
              beerName={beerName}
              onClick={onToggleEdit}
            />
          ) : null}
          {showActions ? (
            <ActionButtons
              candidateKey={candidate.candidateKey}
              actions={actions}
              disabled={actionsDisabled}
              approveBlocked={approveBlocked}
              loadingAction={rowLoading ? loadingDecisionAction : ''}
              onClick={onDecisionClick}
            />
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

BeerIngestionReviewRowActions.propTypes = {
  candidate: PropTypes.object.isRequired,
  decision: PropTypes.object,
  draft: PropTypes.object.isRequired,
  reviewLocked: PropTypes.bool,
  actionLoading: PropTypes.bool,
  loadingCandidateKey: PropTypes.string,
  loadingDecisionAction: PropTypes.string,
  editing: PropTypes.bool,
  approveBlockedMessage: PropTypes.string,
  onDecisionClick: PropTypes.func.isRequired,
  onToggleEdit: PropTypes.func,
};
